import { CohortExplorer, cohortQueryParams, type CohortDimension } from '@affdice/portal-kit';

import type { AffiliateApi } from './client.ts';
import { cohortFixedReason, cohortRange, type CohortPrivacy } from './cohort-privacy.ts';

/** An affiliate's own players: every split but by affiliate, which is always them. */
const AFFILIATE_COHORT_DIMENSIONS: readonly CohortDimension[] = [
  'none', 'campaign', 'brand', 'product', 'country',
  'sub_id_1', 'sub_id_2', 'sub_id_3', 'sub_id_4', 'sub_id_5',
  'sub_id_6', 'sub_id_7', 'sub_id_8', 'sub_id_9', 'sub_id_10',
];

/**
 * §35.3a — the cohort report for the affiliate's own players. The players
 * behind a cell open only where the operator allows player-level reporting;
 * otherwise the server's refusal says so in place of the list. While a brand
 * protects small cohorts the anchor, period, split and sub-ID filter are not
 * offered, and the reason is shown instead.
 */
export function AffiliateCohortPanel({
  api,
  range,
  now,
  privacy = null,
}: {
  api: AffiliateApi;
  range: { from: string; to: string; brand_id?: string };
  now: Date;
  privacy?: CohortPrivacy | null;
}) {
  const asked = cohortRange(range, privacy);
  const query: Record<string, string> = {
    from: asked.from, to: asked.to, ...(asked.brand_id ? { brand_id: asked.brand_id } : {}),
  };
  const fixedReason = cohortFixedReason(privacy);
  return (
    <CohortExplorer
      load={(q) => api.cohortMatrix({ ...query, ...cohortQueryParams(q) })}
      loadPlayers={(q, cell) => api.cohortMatrixPlayers({ ...query, ...cohortQueryParams(q), ...cell })}
      dimensions={AFFILIATE_COHORT_DIMENSIONS}
      refreshKey={`${asked.from}|${asked.to}|${asked.brand_id ?? ''}|${fixedReason ?? ''}`}
      now={now}
      {...(fixedReason ? { fixedReason } : {})}
    />
  );
}
